import React, { useRef, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import cx from 'clsx'
import YouMayAlsoLikeOffersCarousel from 'components/Recommendations/YouMayAlsoLike/Shared/YouMayAlsoLikeOffersCarousel'
import YouMayAlsoLikeHeader from 'components/Recommendations/YouMayAlsoLike/Shared/YouMayAlsoLikeHeader'
import LayoutContainer from 'components/Common/LayoutContainer/index'
import rem from 'polished/lib/helpers/rem'
import zIndex from 'styles/tools/z-index'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDistanceUnit, getExpiredOfferRecommendationsState } from 'selectors/recommendations'
import { DistanceUnit } from 'lib/geo/distanceUnits'
import useOffers from 'hooks/Offers/useOffers'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

const Root = styled.div`
  width: 100%;
  transition: max-height .3s ease-out;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  padding-top: ${rem(24)};
  padding-bottom: ${rem(32)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  z-index: ${zIndex.stickyHeader};

  &.minimised {
    overflow-y: hidden;
  }
  &.hidden {
    display: none;
  }
`

interface Props {
  shouldShowRecommendations: boolean;
  offerIds: Array<string>;
  hideOffers: boolean;
  distanceUnit: DistanceUnit;
}

function UnavailableYouMayAlsoLike(props: Props) {
  const { hideOffers, shouldShowRecommendations, offerIds, distanceUnit } = props
  const rootRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLDivElement>(null)
  const [minimised, setMinimised] = useState(false)
  const [offers] = useOffers<App.Offer>(offerIds)

  const tracking = useOfferListTracking(
    'LERE - Expired Deals Header',
  )

  const onToggleHide = useCallback(() => {
    setMinimised(!minimised)
  }, [minimised, setMinimised])

  const style = useMemo<React.CSSProperties>(() => {
    if (minimised && headingRef.current && rootRef.current) {
      const computedRootStyle = getComputedStyle(rootRef.current)
      const maxHeight = headingRef.current.getBoundingClientRect().height +
        parseFloat(computedRootStyle.paddingTop) + 12
      return {
        maxHeight: `${maxHeight}px`,
      }
    }
    return {}
  }, [minimised])

  if (!shouldShowRecommendations) {
    return null
  }

  return (
    <Root ref={rootRef} className={cx({ minimised, hidden: hideOffers })} style={style} data-testid="lere-sticky-header">
      <LayoutContainer>
        <YouMayAlsoLikeHeader
          expandDirection="down"
          onToggleMinimise={onToggleHide}
          minimised={minimised}
          ref={headingRef}
        />
        <OfferListEventsProvider tracking={tracking}>
          <YouMayAlsoLikeOffersCarousel
            offers={offers}
            offerDistanceUnit={distanceUnit}
            pageSize={3}
          />
        </OfferListEventsProvider>
      </LayoutContainer>
    </Root>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const offerId = state?.offer?.id
  const offersState = offerId ? getExpiredOfferRecommendationsState(appState, offerId) : { displayMinLimit: false, offerIds: [] as Array<string> }

  return {
    currency: appState.geo.currentCurrency,
    shouldShowRecommendations: offersState.displayMinLimit,
    offerIds: offersState.offerIds,
    distanceUnit: getDistanceUnit(appState),
  }
}, UnavailableYouMayAlsoLike)
